import React from 'react';
import { useLazyCommand } from '@soap/modules';
import {
    getActiveStepIndex,
    getActiveStepNumber,
    getEffectiveInstructionSteps,
    hasDetailedInstructionSteps
} from './instructionStepUtils';
import type { InstructionStepDto } from './types';

const DEBOUNCE_MS = 300;

export function useInstructionStepCarousel(args: {
    unitId: string;
    lessonId: string;
    sessionId?: number | null;
    steps?: InstructionStepDto[];
    enabled: boolean;
}) {
    const [sendSetActiveStep] = useLazyCommand('C324v1_SetActiveInstructionStep');
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Step number the user last navigated to locally; stale E316 responses are ignored until server confirms it. */
    const pendingStepNumberRef = React.useRef<number | null>(null);
    const carouselScopeRef = React.useRef(`${args.lessonId}:${args.sessionId ?? ''}`);

    const effectiveSteps = React.useMemo(
        () => getEffectiveInstructionSteps(args.steps),
        [args.steps]
    );

    const canUseCarousel = args.enabled && effectiveSteps.length > 0 && hasDetailedInstructionSteps(effectiveSteps);

    const [isFlipped, setIsFlipped] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(() => getActiveStepIndex(effectiveSteps));
    /** Skip auto-open on first paint so remounting an already in-progress lesson stays on overview (expandable). */
    const prevCanUseCarouselRef = React.useRef<boolean | null>(null);

    React.useEffect(() => {
        const scope = `${args.lessonId}:${args.sessionId ?? ''}`;
        if (carouselScopeRef.current !== scope) {
            carouselScopeRef.current = scope;
            pendingStepNumberRef.current = null;
        }
    }, [args.lessonId, args.sessionId]);

    React.useEffect(() => {
        const serverIndex = getActiveStepIndex(effectiveSteps);
        const serverStepNumber = getActiveStepNumber(effectiveSteps);
        const pending = pendingStepNumberRef.current;

        if (pending != null) {
            if (serverStepNumber !== pending) {
                return;
            }
            pendingStepNumberRef.current = null;
        }

        setCurrentIndex(serverIndex);
    }, [effectiveSteps]);

    React.useEffect(() => {
        if (prevCanUseCarouselRef.current === null) {
            prevCanUseCarouselRef.current = canUseCarousel;
            return;
        }
        // Open steps when they first become available after Start (false → true), not on page load.
        if (canUseCarousel && !prevCanUseCarouselRef.current) {
            setIsFlipped(true);
        }
        prevCanUseCarouselRef.current = canUseCarousel;
    }, [canUseCarousel]);

    const persistStep = React.useCallback(
        (index: number) => {
            const step = effectiveSteps[index];
            if (!step?.e251_StepNumber || !args.unitId || !args.lessonId) return;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (args.sessionId == null) return;
                void sendSetActiveStep({
                    C324_UnitId: args.unitId,
                    C324_LessonId: args.lessonId,
                    C324_SessionId: args.sessionId,
                    C324_StepNumber: step.e251_StepNumber
                });
            }, DEBOUNCE_MS);
        },
        [args.unitId, args.lessonId, args.sessionId, effectiveSteps, sendSetActiveStep]
    );

    const handleIndexChange = React.useCallback(
        (index: number) => {
            const stepNumber = effectiveSteps[index]?.e251_StepNumber;
            if (stepNumber != null) {
                pendingStepNumberRef.current = stepNumber;
            }
            setCurrentIndex(index);
            persistStep(index);
        },
        [persistStep, effectiveSteps]
    );

    React.useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    return {
        effectiveSteps,
        canUseCarousel,
        isFlipped,
        setIsFlipped,
        currentIndex,
        handleIndexChange,
        currentStep: effectiveSteps[currentIndex]
    };
}
